import ReactGA from 'react-ga'
import URL from 'url-parse'
import { objectEntries, objectValues } from './Object'
import GATracker from './GATracker'

/**
 * Google Analytics Helper Class
 */
class GA {
  /**
   * Constructor
   * @param {Object} params
   * @param {array} params.trackers List of trackers used
   * @param {Object} params.config Configuration options for GA
   * @param {bool} params.config.debug Optional. If set to true, will output additional feedback to the console
   * @param {bool} params.config.titleCase Optional. Defaults to true. If set to false, strings will not be converted to title case before sending to GA
   * @param {string} params.config.name Name of the tracker object
   * @param {string} params.config.cookieDomain Specifies the domain used to store the analytics cookie. Setting this to 'none' sets the cookie without specifying a domain
   */
  constructor ({ trackers, ...config } = {}) {
    this.reactGA = ReactGA

    this.trackers = {}

    objectEntries(trackers).forEach(([ trackerKey, trackerSettings ]) => {
      this.trackers[trackerKey] = new GATracker(this.reactGA, trackerSettings)
    })

    this.initializeReactGA(objectValues(trackers), config)
  }

  initializeReactGA (trackers, config) {
    const {
      alwaysSendToDefaultTracker,
      cookieDomain,
      debug,
      titleCase
    } = config

    const initOptions = {
      alwaysSendToDefaultTracker: alwaysSendToDefaultTracker || this.shouldAlwaysSendToDefaultTracker(),
      debug: debug || false,
      titleCase: titleCase || true
    }

    trackers.forEach(tracker => {
      tracker.gaOptions = Object.assign({}, tracker.gaOptions, {
        cookieDomain: cookieDomain || `.${new URL(window.location.href).hostname}`
      })
    })
    this.reactGA.initialize(trackers, initOptions)
  }

  shouldAlwaysSendToDefaultTracker () {
    const trackersArray = objectValues(this.trackers)
    if (trackersArray.length > 1) {
      return false
    } else if (trackersArray.length === 1) {
      return !trackersArray[0].name
    }
  }

  hasTracker (trackerKey) {
    return this.trackers[trackerKey] instanceof GATracker
  }

  getTracker (trackerKey) {
    return this.hasTracker(trackerKey) ? this.trackers[trackerKey] : null
  }

  requireGlobal (pluginName, pluginOptions) {
    this.reactGA.plugin.require(pluginName, pluginOptions)
  }
}

export default GA
